import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpTooltip from './HelpTooltip';

// Utility: parse supported video links (YouTube, Vimeo, direct MP4/WebM)
export const parseVideoUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const url = rawUrl.trim();

  // 1. YouTube
  const ytMatch = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/i);
  if (ytMatch && ytMatch[2].length === 11) {
    const videoId = ytMatch[2];
    return {
      platform: 'youtube',
      videoId,
      url,
      embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
      thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      fallbackThumb: `https://img.youtube.com/vi/${videoId}/0.jpg`,
    };
  }

  // 2. Vimeo
  const vimeoMatch = url.match(/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i);
  if (vimeoMatch && vimeoMatch[1]) {
    const videoId = vimeoMatch[1];
    return {
      platform: 'vimeo',
      videoId,
      url,
      embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=1`,
      thumbnail: `https://vumbnail.com/${videoId}.jpg`,
      fallbackThumb: '',
    };
  }

  // 3. Direct video file (MP4, WebM, OGG, MOV)
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)) {
    return {
      platform: 'direct',
      videoId: '',
      url,
      embedUrl: url,
      thumbnail: '',
      fallbackThumb: '',
    };
  }

  return null;
};

export default function PrayerVideoManager({
  videos = [],
  onVideosChange,
  dayNumber = 1,
  isLight = false,
  extraControls = null,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);

  // Form states for Add / Edit
  const [editingId, setEditingId] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [titleInput, setTitleInput] = useState('');
  const [parsedInfo, setParsedInfo] = useState(null);
  const [urlError, setUrlError] = useState('');
  const [fetchingMeta, setFetchingMeta] = useState(false);

  // Parse and fetch metadata automatically whenever urlInput changes
  useEffect(() => {
    if (!urlInput.trim()) {
      setParsedInfo(null);
      setUrlError('');
      return;
    }

    const parsed = parseVideoUrl(urlInput);
    if (!parsed) {
      setParsedInfo(null);
      setUrlError('Invalid or unsupported video link. Please enter a valid YouTube, Vimeo, or direct MP4 link.');
      return;
    }

    setUrlError('');
    setParsedInfo(parsed);

    // Automatically retrieve video title & thumbnail from oEmbed API if title is blank or user is adding new video
    if (!titleInput.trim()) {
      setFetchingMeta(true);
      const oembedUrl = `https://noembed.com/embed?url=${encodeURIComponent(parsed.url)}`;
      fetch(oembedUrl)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.title) {
            setTitleInput(data.title);
          }
          if (data && data.thumbnail_url && !parsed.thumbnail) {
            setParsedInfo((prev) => (prev ? { ...prev, thumbnail: data.thumbnail_url } : prev));
          }
        })
        .catch(() => {
          // Fallback title based on platform
          if (parsed.platform === 'youtube') {
            setTitleInput(`Prayer Video (YouTube: ${parsed.videoId})`);
          }
        })
        .finally(() => {
          setFetchingMeta(false);
        });
    }
  }, [urlInput]);

  const handleOpenAdd = () => {
    setEditingId(null);
    setUrlInput('');
    setTitleInput('');
    setParsedInfo(null);
    setUrlError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (vid) => {
    setEditingId(vid.id);
    setUrlInput(vid.url || '');
    setTitleInput(vid.title || '');
    setParsedInfo(parseVideoUrl(vid.url) || null);
    setUrlError('');
    setDialogOpen(true);
  };

  const handleSaveVideo = () => {
    if (!parsedInfo) {
      setUrlError('Please provide a valid video link.');
      return;
    }

    const currentVideos = Array.isArray(videos) ? [...videos] : [];
    const fallbackTitle = titleInput.trim() || `Day ${dayNumber} Video ${currentVideos.length + 1}`;

    if (editingId) {
      // Update existing video
      const updated = currentVideos.map((v) => {
        if (v.id === editingId) {
          return {
            ...v,
            url: parsedInfo.url,
            platform: parsedInfo.platform,
            videoId: parsedInfo.videoId,
            thumbnail: parsedInfo.thumbnail || v.thumbnail,
            title: fallbackTitle,
          };
        }
        return v;
      });
      onVideosChange && onVideosChange(updated);
    } else {
      // Add new video
      const newVideo = {
        id: `vid_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        url: parsedInfo.url,
        platform: parsedInfo.platform,
        videoId: parsedInfo.videoId,
        thumbnail: parsedInfo.thumbnail || '',
        title: fallbackTitle,
        is_published: true,
        order: currentVideos.length + 1,
      };
      onVideosChange && onVideosChange([...currentVideos, newVideo]);
    }

    setDialogOpen(false);
  };

  const handleDeleteVideo = (id) => {
    const currentVideos = Array.isArray(videos) ? [...videos] : [];
    const filtered = currentVideos.filter((v) => v.id !== id);
    // Re-index order
    const reordered = filtered.map((v, idx) => ({ ...v, order: idx + 1 }));
    onVideosChange && onVideosChange(reordered);
  };

  const handleTogglePublish = (id) => {
    const currentVideos = Array.isArray(videos) ? [...videos] : [];
    const updated = currentVideos.map((v) => (v.id === id ? { ...v, is_published: !v.is_published } : v));
    onVideosChange && onVideosChange(updated);
  };

  const handleMoveOrder = (index, direction) => {
    const currentVideos = Array.isArray(videos) ? [...videos] : [];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= currentVideos.length) return;

    const temp = currentVideos[index];
    currentVideos[index] = currentVideos[targetIndex];
    currentVideos[targetIndex] = temp;

    const reordered = currentVideos.map((v, idx) => ({ ...v, order: idx + 1 }));
    onVideosChange && onVideosChange(reordered);
  };

  const handlePlayPreview = (vid) => {
    setPreviewVideo(vid);
    setPreviewModalOpen(true);
  };

  const videoList = Array.isArray(videos) ? videos : [];

  return (
    <Box sx={{ width: '100%', mt: 1.5, mb: 2 }}>
      {/* ── Top Bar: All Controls in One Line (Translate, Image, Video) ── */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
        sx={{
          p: 1.2,
          px: 1.8,
          borderRadius: "10px",
          bgcolor: isLight ? "rgba(15, 118, 110, 0.04)" : "rgba(255, 255, 255, 0.03)",
          border: isLight ? "1px solid rgba(15, 118, 110, 0.18)" : "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
          {extraControls}

          <Box display="flex" alignItems="center" gap={0.8}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VideoLibraryIcon sx={{ color: isLight ? '#0F766E' : '#2DD4BF', fontSize: 18 }} />}
              onClick={handleOpenAdd}
              sx={{
                borderColor: isLight ? '#0D9488' : '#2DD4BF',
                color: isLight ? '#0F766E' : '#FFFFFF',
                backgroundColor: isLight ? 'rgba(15, 118, 110, 0.08)' : 'rgba(45, 212, 191, 0.16)',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.84rem',
                borderRadius: '8px',
                height: 36,
                px: 2,
                boxShadow: isLight ? 'none' : '0 1px 4px rgba(0,0,0,0.25)',
                '&:hover': {
                  borderColor: isLight ? '#0D9488' : '#38BDF8',
                  backgroundColor: isLight ? 'rgba(15, 118, 110, 0.16)' : 'rgba(45, 212, 191, 0.28)',
                  color: '#FFFFFF',
                },
              }}
            >
              Add Video (Link)
            </Button>
            <HelpTooltip
              title="Add Video Link"
              purpose="Attach worship videos, sermon clips, or devotional streams to this prayer day."
              howItWorks="Supports YouTube, Vimeo, and direct MP4 video URLs. Plays seamlessly inside the app."
              mobileEffect="Displays an interactive media slider and in-app video player for praying intercessors."
            />

            {videoList.length > 0 && (
              <Chip
                size="small"
                label={`${videoList.length} ${videoList.length === 1 ? 'Video' : 'Videos'} Added`}
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              />
            )}
          </Box>
        </Box>

        <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.6)', ml: { xs: 0, md: 'auto' } }}>
          Supports YouTube, Vimeo, & MP4 links. Displays with slider if image is also added.
        </Typography>
      </Box>

      {/* ── Video Cards List ── */}
      {videoList.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {videoList.map((vid, idx) => {
            const parsed = parseVideoUrl(vid.url);
            const thumb = vid.thumbnail || parsed?.thumbnail;
            const isYt = vid.platform === 'youtube' || parsed?.platform === 'youtube';

            return (
              <Box
                key={vid.id || idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  borderRadius: '10px',
                  bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                  border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  gap: 1.5,
                  transition: 'all 0.2s ease',
                  opacity: vid.is_published ? 1 : 0.65,
                  '&:hover': {
                    borderColor: '#0F766E',
                    boxShadow: isLight ? '0 2px 8px rgba(15, 118, 110, 0.08)' : 'none',
                  },
                }}
              >
                {/* Thumbnail & Title */}
                <Box display="flex" alignItems="center" gap={1.8} sx={{ minWidth: 0, flex: 1 }}>
                  {/* Thumbnail with Play Overlay */}
                  <Box
                    onClick={() => handlePlayPreview(vid)}
                    sx={{
                      width: 90,
                      height: 56,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      position: 'relative',
                      bgcolor: '#000000',
                      cursor: 'pointer',
                      flexShrink: 0,
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                    }}
                  >
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={vid.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#1E293B',
                        }}
                      >
                        <VideoLibraryIcon sx={{ color: '#64748B' }} />
                      </Box>
                    )}
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.35)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.2)' },
                      }}
                    >
                      <PlayCircleOutlineIcon sx={{ color: '#FFFFFF', fontSize: 26 }} />
                    </Box>
                  </Box>

                  {/* Title & Link */}
                  <Box sx={{ minWidth: 0 }}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.3}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: isLight ? '#0F172A' : '#FFFFFF',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 260,
                        }}
                      >
                        {vid.title || 'Untitled Video'}
                      </Typography>

                      <Chip
                        size="small"
                        icon={isYt ? <YouTubeIcon sx={{ fontSize: 14, color: '#FF0000 !important' }} /> : <LinkIcon sx={{ fontSize: 13 }} />}
                        label={isYt ? 'YouTube' : vid.platform || 'Video Link'}
                        sx={{
                          height: 20,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          bgcolor: isYt ? 'rgba(255, 0, 0, 0.08)' : 'rgba(15, 118, 110, 0.1)',
                          color: isYt ? '#DC2626' : '#0F766E',
                        }}
                      />

                      <Chip
                        size="small"
                        label={`#${idx + 1}`}
                        sx={{
                          height: 20,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          bgcolor: isLight ? '#E2E8F0' : 'rgba(255, 255, 255, 0.1)',
                          color: isLight ? '#475569' : '#CBD5E1',
                        }}
                      />
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.55)',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 320,
                      }}
                    >
                      {vid.url}
                    </Typography>
                  </Box>
                </Box>

                {/* Controls: Reorder, Publish Switch, Edit, Delete */}
                <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                  {/* Order Up */}
                  <Tooltip title="Move Up">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveOrder(idx, -1)}
                        disabled={idx === 0}
                        sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.7)' }}
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Order Down */}
                  <Tooltip title="Move Down">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveOrder(idx, 1)}
                        disabled={idx === videoList.length - 1}
                        sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.7)' }}
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Publish Switch */}
                  <Tooltip title={vid.is_published ? 'Published (Live on Mobile)' : 'Unpublished (Hidden)'}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!vid.is_published}
                          onChange={() => handleTogglePublish(vid.id)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#0F766E' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0F766E' },
                          }}
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  </Tooltip>

                  {/* Edit */}
                  <Tooltip title="Edit Video Link & Title">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(vid)}
                      sx={{ color: '#0F766E' }}
                    >
                      <EditIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>

                  {/* Delete */}
                  <Tooltip title="Delete Video">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteVideo(vid.id)}
                      sx={{ color: '#DC2626' }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Dialog: Add / Edit Video ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
            bgcolor: isLight ? '#FFFFFF' : '#182232',
            color: isLight ? '#0F172A' : '#FFFFFF',
            border: isLight ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <VideoLibraryIcon sx={{ color: '#0F766E' }} />
            <Typography variant="h6" fontWeight={700}>
              {editingId ? 'Edit Video Details' : 'Add Video to Prayer Closet'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseIcon sx={{ color: isLight ? '#64748B' : '#FFFFFF' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: isLight ? '#E2E8F0' : 'rgba(255, 255, 255, 0.08)' }}>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* Video URL Input */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={0.6}>
                Video Link (YouTube, Vimeo, or MP4) *
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. https://www.youtube.com/watch?v=... or https://youtu.be/..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                error={Boolean(urlError)}
                helperText={urlError || 'Paste public YouTube link, shorts, Vimeo, or direct video file.'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.04)',
                  },
                }}
              />
            </Box>

            {/* Video Title Input */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.6}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Video Title (Optional / Auto-retrieved)
                </Typography>
                {fetchingMeta && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CircularProgress size={13} sx={{ color: '#0F766E' }} />
                    <Typography variant="caption" sx={{ color: '#0F766E' }}>
                      Retrieving title...
                    </Typography>
                  </Box>
                )}
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Custom title to display on mobile..."
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.04)',
                  },
                }}
              />
            </Box>

            {/* Interactive Preview Card before saving */}
            {parsedInfo && !urlError && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  bgcolor: isLight ? '#F0FDFA' : 'rgba(15, 118, 110, 0.1)',
                  border: '1px solid rgba(15, 118, 110, 0.3)',
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center" gap={0.8}>
                    <CheckCircleOutlineIcon sx={{ color: '#0F766E', fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0F766E' }}>
                      Video Validated & Preview Ready
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={parsedInfo.platform.toUpperCase()}
                    sx={{
                      bgcolor: '#0F766E',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      height: 20,
                    }}
                  />
                </Box>

                {/* Interactive Embedded Preview Box */}
                {parsedInfo.platform === 'youtube' && parsedInfo.videoId && (
                  <Box
                    sx={{
                      width: '100%',
                      height: 190,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      bgcolor: '#000000',
                      mb: 1.5,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    }}
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${parsedInfo.videoId}`}
                      title={titleInput || 'Video Preview'}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </Box>
                )}

                {parsedInfo.platform !== 'youtube' && parsedInfo.thumbnail && (
                  <Box
                    sx={{
                      width: '100%',
                      height: 160,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      bgcolor: '#000000',
                      mb: 1.5,
                      position: 'relative',
                    }}
                  >
                    <img
                      src={parsedInfo.thumbnail}
                      alt="Thumbnail preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                )}

                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {titleInput || 'Video Title Ready'}
                </Typography>
                <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.6)' }}>
                  Platform: {parsedInfo.platform} • ID: {parsedInfo.videoId || 'direct-stream'}
                </Typography>
              </Box>
            )}

            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              <strong>Multi-Media Slider:</strong> If you add both an image and video(s), mobile users will be able to swipe between the image and video in a smooth carousel slider.
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.7)', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!parsedInfo || Boolean(urlError)}
            onClick={handleSaveVideo}
            sx={{
              bgcolor: '#0F766E',
              color: '#FFFFFF',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              '&:hover': { bgcolor: '#0D9488' },
            }}
          >
            {editingId ? 'Update Video' : 'Add to Day Content'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Full Video Play Preview ── */}
      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
            bgcolor: '#0F172A',
            color: '#FFFFFF',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {previewVideo?.title || 'Video Player Preview'}
          </Typography>
          <IconButton size="small" onClick={() => setPreviewModalOpen(false)}>
            <CloseIcon sx={{ color: '#FFFFFF' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#000000', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          {previewVideo && (
            <Box sx={{ width: '100%', height: { xs: 260, sm: 380, md: 440 } }}>
              {previewVideo.platform === 'youtube' ? (
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${previewVideo.videoId || parseVideoUrl(previewVideo.url)?.videoId}?autoplay=1`}
                  title={previewVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <iframe
                  width="100%"
                  height="100%"
                  src={previewVideo.url}
                  title={previewVideo.title}
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5, px: 2.5, justifyContent: 'space-between' }}>
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => previewVideo?.url && window.open(previewVideo.url, '_blank')}
            sx={{ color: '#64D2E2', textTransform: 'none', fontWeight: 600 }}
          >
            Open Original Platform
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setPreviewModalOpen(false)}
            sx={{ color: '#FFFFFF', borderColor: 'rgba(255, 255, 255, 0.3)', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
